import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {DataObjectId, GenericStickerState, Hub} from 'yougile-model'
import {getTemplate} from '@components/movement-templates/helpers/get-template'
import {createUseStore} from '@containers/hooks'
import {Sticker} from '@extensions/sticker/sticker'
import {canCreateTask} from './can-create-task'
import {createTask} from './create-task'
import {getRequiredStickers} from './get-required-stickers'
import {CreateTaskProps, RequiredSticker, ResetProps, State} from './types'

const initState: State = {
  text: '',
  isTextEditShowed: false,
  requiredStickers: [],
  error: '',
  isTriedCreateTask: false
}

export class AddTaskStore {
  state: State = initState
  board: Hub

  constructor(board: Hub, columnId: DataObjectId) {
    this.board = board
    this.state.columnId = columnId
    board && (this.state.requiredStickers = getRequiredStickers({board}))

    makeAutoObservable(this, {state: observable.deep}, {autoBind: true})
  }

  syncOnUpdate(columnId: DataObjectId): void {
    this.state.columnId = columnId
  }

  show(): void {
    this.state.isTextEditShowed = true
  }

  showWithTemplateId(templateId: DataObjectId): void {
    this.state.isTextEditShowed = true
    this.state.templateId = templateId
  }

  error(error: string): void {
    this.state.error = error
  }

  updateText(text: string): void {
    this.state.text = text
  }

  get hasRequiredStickers(): boolean {
    return !!this.state.requiredStickers.length
  }

  get assignUserRequiredSticker(): RequiredSticker | undefined {
    return this.state.requiredStickers.find(
      sticker => Sticker.getType(sticker.tag) === 'user'
    )
  }

  get requiredStickersExcludeAssignUser(): RequiredSticker[] {
    return this.state.requiredStickers.filter(
      sticker => Sticker.getType(sticker.tag) !== 'user'
    )
  }

  changeSticker(id: DataObjectId, value?: GenericStickerState): void {
    const found = this.state.requiredStickers.find(st => st.tag.id === id)
    if (found) {
      found.defaultState = value
    }
  }

  reset(props: ResetProps = {}): boolean | void {
    const {needClose = false, resetStickers = 'none'} = props

    this.state.text = ''
    this.state.error = ''
    this.state.isTriedCreateTask = false
    const board = this.board

    if (props.resetTemplateId) {
      this.state.templateId = undefined
    }

    const templateId = this.state.templateId
    const template = getTemplate(templateId)
    const templateStickers = template?.stickers

    if (resetStickers === 'all') {
      this.state.requiredStickers = getRequiredStickers({
        board,
        templateStickers
      })
    }

    if (resetStickers === 'required') {
      const initialStickers = getRequiredStickers({board, templateStickers})

      const requiredStickers = this.state.requiredStickers

      for (const initialSticker of initialStickers) {
        if (initialSticker.required !== 'require') {
          const found = requiredStickers.find(
            r => r.tag.id === initialSticker.tag.id
          )
          initialSticker.defaultState = found?.defaultState
        }
      }
      this.state.requiredStickers = initialStickers
    }

    const open = this.state.isTextEditShowed
    if (needClose && open) {
      this.state.isTextEditShowed = false
      return true
    }
  }

  createTask(props: CreateTaskProps = {}): void {
    const text = this.state.text.trim()
    if (!text) {
      this.state.error = Lang.msg.addTaskEmptyTextErrorMessage
      this.state.isTriedCreateTask = true
      return
    }
    if (!canCreateTask(this.state.requiredStickers)) {
      this.state.error = Lang.msg.addTaskStickerErrorMessage
      this.state.isTriedCreateTask = true
      return
    }

    const taskId = createTask({
      text,
      stickers: this.state.requiredStickers,
      columnId: this.state.columnId,
      toEnd: props.toEnd,
      templateId: this.state.templateId
    })
    this.state.onAdd?.(taskId)

    const needClose = !!this.state.templateId
    this.reset({...props, resetTemplateId: true, needClose})
  }

  createTaskOnBlur(toEnd = false): void {
    const {requiredStickers, columnId} = this.state
    const text = this.state.text.trim()

    if (text) {
      if (!canCreateTask(this.state.requiredStickers)) {
        this.state.error = Lang.msg.addTaskStickerErrorMessage
        this.state.isTriedCreateTask = true
        return
      }

      const taskId = createTask({
        text,
        stickers: requiredStickers,
        columnId,
        toEnd,
        templateId: this.state.templateId
      })
      this.state.onAdd?.(taskId)
    }

    this.reset({
      needClose: true,
      resetStickers: 'all' as const,
      resetTemplateId: true
    })
  }

  onTextEditBlur(toEnd?: boolean): void {
    if (this.hasRequiredStickers) {
      return
    }

    if (!this.state.text) {
      this.reset({
        needClose: true,
        resetStickers: 'all',
        resetTemplateId: true
      })
      return
    }

    this.createTask({
      needClose: true,
      resetStickers: 'all',
      toEnd,
      resetTemplateId: true
    })
  }

  setOnAdd(onAdd?: (taskId?: DataObjectId) => void): void {
    this.state.onAdd = onAdd
  }
}

const [AddTaskContext, useAddTaskStore] = createUseStore(AddTaskStore)
export {AddTaskContext, useAddTaskStore}
